// src/context/authContext.js
import { createContext, useContext, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import api from '../api/axios';
import {
  selectAuth,
  login as reduxLogin,
  register as reduxRegister,
  logout as reduxLogout,
  getProfile as reduxGetProfile,
  updateUser as reduxUpdateUser,
  setInitialized,
  clearVerification,
  verifyEmail as reduxVerifyEmail,
  resendVerification as reduxResendVerification,
} from '../store/slices/authSlice';
import { showSuccess, showError, showInfo } from '../store/slices/alertSlice';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const dispatch = useAppDispatch();
  const { token, user, isAuthenticated, isLoading, isInitialized, requiresVerification } = useAppSelector(selectAuth);
  
  const [authToken, setAuthToken] = useState(token);
  const [authUser, setAuthUser] = useState(user);
  const [authLoading, setAuthLoading] = useState(true);

  // Initialize auth on mount - ONLY ONCE
  useEffect(() => {
    const initAuth = async () => {
      // If already initialized, don't run again
      if (isInitialized) {
        setAuthLoading(false);
        return;
      }

      // If we have a token but no user, try to get profile
      if (token && !isAuthenticated) {
        try {
          await dispatch(reduxGetProfile()).unwrap();
        } catch (error) {
          console.error('Auth initialization failed:', error);
          // If token is invalid, clear it
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('user');
          setAuthToken(null);
          setAuthUser(null);
        }
      }
      
      dispatch(setInitialized());
      setAuthLoading(false);
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update local state when Redux state changes
  useEffect(() => {
    setAuthToken(token);
    setAuthUser(user);
    setAuthLoading(isLoading);
  }, [token, user, isLoading]);

  /**
   * Login user - Handles verification check
   */
  const login = async (email, password, remember = false) => {
    try {
      const result = await dispatch(reduxLogin({ email, password, remember })).unwrap();
      dispatch(showSuccess('Welcome back! Login successful.'));
      setAuthToken(result.token);
      setAuthUser(result.user);
      return { success: true, ...result };
    } catch (error) {
      // Check if error is due to unverified email
      if (error?.requiresVerification) {
        localStorage.setItem('verificationEmail', email);
        return { 
          success: false, 
          requiresVerification: true,
          message: error.message || 'Please verify your email address before logging in.'
        };
      }
      
      const message = typeof error === 'string' ? error : (error?.message || 'Login failed. Please try again.');
      dispatch(showError(message));
      return { success: false, message };
    }
  };

  /**
   * Register a new user - No auto-login
   */
  const register = async (userData) => {
    try {
      console.log('📝 Sending registration data:', userData);
      
      const response = await api.post('/auth/register', userData);
      
      console.log('📝 Registration response:', response.data);

      if (response.data.success) {
        const { user, requiresVerification, emailSent } = response.data.data;
        
        // Store email for verification
        if (user && user.email) {
          localStorage.setItem('verificationEmail', user.email);
        }
        
        // Clear any existing auth data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');
        
        setAuthToken(null);
        setAuthUser(null);
        
        // If verification is required, redirect to verification page
        if (requiresVerification) {
          return { 
            success: true, 
            message: 'Registration successful! Please check your email to verify your account.',
            requiresVerification: true,
            emailSent: emailSent || false,
            email: user?.email,
          };
        }
        
        return { 
          success: true, 
          message: response.data.message || 'Registration successful',
          user,
        };
      } else {
        return { 
          success: false, 
          message: response.data.message || 'Registration failed. Please try again.',
          errors: response.data.errors || null
        };
      }
    } catch (error) {
      console.error('❌ Registration error details:', error);
      console.error('❌ Error response:', error.response?.data);
      
      let message = 'Registration failed. Please try again.';
      let errors = null;
      
      if (error.response) {
        if (error.response.data?.errors) {
          errors = error.response.data.errors;
          const firstError = Object.values(errors)[0];
          message = typeof firstError === 'string' ? firstError : (firstError || 'Validation error');
        } else {
          message = error.response.data?.message || `Server error (${error.response.status})`;
        }
      } else if (error.request) {
        message = 'No response from server. Please check your connection.';
      } else {
        message = error.message || 'An error occurred during registration.';
      }
      
      return { 
        success: false, 
        message,
        errors
      };
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      await dispatch(reduxLogout()).unwrap();
      dispatch(showInfo('Logged out successfully'));
      setAuthToken(null);
      setAuthUser(null);
    } catch (error) {
      dispatch(showInfo('Logged out'));
      setAuthToken(null);
      setAuthUser(null);
    }
  };

  /**
   * Get current user profile
   */
  const getProfile = async () => {
    try {
      return await dispatch(reduxGetProfile()).unwrap();
    } catch (error) {
      console.error('Get profile error:', error);
      return null;
    }
  };

  /**
   * Update user profile using Redux
   */
  const updateProfile = async (userData) => {
    try {
      const result = await dispatch(reduxUpdateUser(userData));
      dispatch(showSuccess('Profile updated successfully'));
      setAuthUser(result.payload);
      // Update storage
      if (localStorage.getItem('user')) {
        localStorage.setItem('user', JSON.stringify(result.payload));
      } else if (sessionStorage.getItem('user')) {
        sessionStorage.setItem('user', JSON.stringify(result.payload));
      }
      return { success: true, user: result.payload };
    } catch (error) {
      dispatch(showError('Failed to update profile'));
      return { success: false, message: 'Failed to update profile' };
    }
  };

  /**
   * Update user directly via API (for profile photo, etc.)
   * Uses the /api/users/me endpoint
   */
  const updateUser = async (userData) => {
    try {
      console.log('📤 Updating user with data:', userData);
      
      // Use the /users/me endpoint for updating current user
      const response = await api.put('/users/me', userData);
      
      console.log('📥 Update response:', response.data);
      
      if (response.data.success) {
        const updatedUser = response.data.data.user;
        // Update local state
        setAuthUser(updatedUser);
        // Update localStorage
        if (localStorage.getItem('user')) {
          localStorage.setItem('user', JSON.stringify(updatedUser));
        } else if (sessionStorage.getItem('user')) {
          sessionStorage.setItem('user', JSON.stringify(updatedUser));
        }
        dispatch(showSuccess('Profile updated successfully'));
        return { success: true, user: updatedUser };
      }
      dispatch(showError(response.data.message || 'Update failed'));
      return { success: false, message: response.data.message || 'Update failed' };
    } catch (error) {
      console.error('❌ Update user error:', error);
      console.error('❌ Error response:', error.response?.data);
      const message = error.response?.data?.message || 'Update failed';
      dispatch(showError(message));
      return { success: false, message };
    }
  };

  /**
   * Verify email
   */
  const verifyEmail = async (token) => {
    try {
      const result = await dispatch(reduxVerifyEmail(token)).unwrap();
      dispatch(showSuccess('Email verified successfully!'));
      return { success: true, ...result };
    } catch (error) {
      const message = typeof error === 'string' ? error : (error?.message || 'Verification failed');
      dispatch(showError(message));
      return { success: false, message };
    }
  };

  /**
   * Resend verification email
   */
  const resendVerification = async (email) => {
    try {
      const result = await dispatch(reduxResendVerification(email)).unwrap();
      dispatch(showSuccess('Verification email sent!'));
      return { success: true, ...result };
    } catch (error) {
      const message = typeof error === 'string' ? error : (error?.message || 'Failed to resend verification');
      dispatch(showError(message));
      return { success: false, message };
    }
  };

  const value = {
    token: authToken,
    user: authUser,
    isAuthenticated: !!authToken && !!authUser,
    loading: authLoading || isLoading,
    isInitialized,
    requiresVerification,
    login,
    register,
    logout,
    getProfile,
    updateProfile,
    updateUser,
    verifyEmail,
    resendVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;